import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Check, Building2, Package, Tag, ShoppingCart, Wallet, ArrowRight, Rocket } from "lucide-react";

const STEPS = [
  { key: "business", icon: Building2, title: "Dados da empresa", desc: "Nome, moeda e logo da sua organização.", to: "/configuracoes", cta: "Configurar empresa" },
  { key: "lots", icon: Package, title: "Primeiro lote", desc: "Crie o primeiro lote para organizar suas compras.", to: "/lotes", cta: "Criar lote" },
  { key: "items", icon: Tag, title: "Mercadorias", desc: "Cadastre ou importe as peças do seu lote.", to: "/mercadorias", cta: "Adicionar mercadorias" },
  { key: "financial", icon: Wallet, title: "Setup financeiro", desc: "Defina categorias e regras de despesas.", to: "/despesas", cta: "Configurar despesas" },
  { key: "sales", icon: ShoppingCart, title: "Primeira venda", desc: "Registre uma venda e veja os indicadores.", to: "/vendas", cta: "Registrar venda" },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [finishing, setFinishing] = useState(false);
  const [lotCode, setLotCode] = useState("");
  const [creatingLot, setCreatingLot] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/onboarding/status");
      setStatus(data);
      if (data.completed) navigate("/");
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const createLot = async () => {
    if (!lotCode.trim()) { toast.error("Informe um código para o lote."); return; }
    setCreatingLot(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.post("/lots", { code: lotCode.trim(), entry_date: today });
      toast.success("Lote criado!");
      setLotCode("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setCreatingLot(false); }
  };

  const finish = async () => {
    setFinishing(true);
    try {
      await api.post("/onboarding/complete");
      toast.success("Tudo pronto! Bem-vindo ao TRIA.");
      navigate("/");
    } catch (e) { toast.error(apiError(e)); } finally { setFinishing(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const steps = status?.steps || {};
  const doneCount = STEPS.filter((s) => steps[s.key]).length;
  const pct = Math.round((doneCount / STEPS.length) * 100);

  return (
    <div className="max-w-3xl mx-auto" data-testid="onboarding-page">
      <div className="mb-6">
        <div className="inline-flex items-center gap-2 text-accent font-semibold text-sm mb-2"><Rocket className="h-4 w-4" /> Primeiros passos</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Vamos configurar sua operação</h1>
        <p className="text-muted-foreground mt-1">Complete os passos abaixo para tirar o máximo do TRIA. Você pode concluir a qualquer momento.</p>
      </div>

      <div className="rounded-xl border border-border bg-card p-5 mb-6">
        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="font-medium">{doneCount} de {STEPS.length} concluídos</span>
          <span className="font-num text-muted-foreground">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-accent transition-all" style={{ width: `${Math.max(4, pct)}%` }} /></div>
      </div>

      <div className="space-y-3">
        {STEPS.map((s) => {
          const done = steps[s.key];
          return (
            <div key={s.key} className={`flex items-center gap-4 rounded-xl border p-4 ${done ? "border-emerald-300 bg-emerald-50/50" : "border-border bg-card"}`} data-testid={`onboarding-step-${s.key}`}>
              <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                {done ? <Check className="h-5 w-5" /> : <s.icon className="h-5 w-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{s.title}</p>
                <p className="text-sm text-muted-foreground">{s.desc}</p>
              </div>
              {s.key === "lots" && !done ? (
                <div className="flex items-center gap-2">
                  <input value={lotCode} onChange={(e) => setLotCode(e.target.value)} placeholder="Código do lote" className="h-9 rounded-md border border-border bg-background px-3 text-sm w-[150px]" data-testid="onboarding-lot-code" />
                  <Button onClick={createLot} disabled={creatingLot} data-testid="onboarding-create-lot">
                    {creatingLot ? <Loader2 className="h-4 w-4 animate-spin" /> : "Criar lote"}
                  </Button>
                </div>
              ) : (
                <Button variant={done ? "ghost" : "outline"} onClick={() => navigate(s.to)} data-testid={`onboarding-cta-${s.key}`}>
                  {done ? "Revisar" : s.cta} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-6">
        <Button onClick={finish} disabled={finishing} data-testid="onboarding-finish-button">
          {finishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          {doneCount === STEPS.length ? "Concluir e ir ao painel" : "Pular e concluir depois"}
        </Button>
      </div>
    </div>
  );
}
